import { ExpandMoreRounded as ExpandIcon } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";

const MIN_REASON_LENGTH = 4;

const SEVERITY_COLOURS = {
  error: { border: "error.main", chip: "error" },
  warning: { border: "warning.main", chip: "warning" },
};

const IssueItem = ({ issue, severity }) => {
  const colours = SEVERITY_COLOURS[severity];
  return (
    <ListItem
      sx={{
        display: "block",
        borderLeft: 4,
        borderColor: colours.border,
        paddingLeft: 2,
        marginBottom: 1,
        bgcolor: "background.paper",
      }}
    >
      <Stack direction={"row"} gap={1} alignItems={"center"} flexWrap={"wrap"}>
        <Chip
          label={issue.code}
          color={colours.chip}
          size={"small"}
          variant={"outlined"}
        />
        {issue.part && (
          <Typography variant={"caption"} color={"text.secondary"}>
            {issue.part}
          </Typography>
        )}
      </Stack>
      <Typography variant={"body2"} sx={{ marginTop: 0.5 }}>
        {issue.message}
      </Typography>
      {issue.tag && (
        <Typography
          component={"pre"}
          variant={"body2"}
          sx={{
            marginTop: 0.5,
            marginBottom: 0,
            padding: 1,
            bgcolor: "grey.100",
            borderRadius: 1,
            overflowX: "auto",
            whiteSpace: "pre-wrap",
          }}
        >
          <code>{issue.tag}</code>
        </Typography>
      )}
    </ListItem>
  );
};

const ReportSection = ({ report }) => {
  const errors = report.errors ?? [];
  const warnings = report.warnings ?? [];
  const checkedNames = report.checkedNames ?? [];
  const title =
    report.templateTitle ?? report.templateId ?? "Untitled template";
  return (
    <Stack gap={1}>
      <Typography variant={"h6"} component={"h3"}>
        {title}
      </Typography>
      {errors.length === 0 && warnings.length === 0 && (
        <Alert severity={"success"} variant={"outlined"}>
          {"No problems were found in this template."}
        </Alert>
      )}
      {(errors.length > 0 || warnings.length > 0) && (
        <List disablePadding>
          {errors.map((issue, index) => (
            <IssueItem
              key={`error-${index}`}
              issue={issue}
              severity={"error"}
            />
          ))}
          {warnings.map((issue, index) => (
            <IssueItem
              key={`warning-${index}`}
              issue={issue}
              severity={"warning"}
            />
          ))}
        </List>
      )}
      <Accordion disableGutters elevation={0} variant={"outlined"}>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Typography variant={"body2"}>{"Names checked"}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {checkedNames.length === 0 ? (
            <Typography variant={"body2"} color={"text.secondary"}>
              {"No names were checked."}
            </Typography>
          ) : (
            <Stack direction={"row"} gap={0.5} flexWrap={"wrap"}>
              {checkedNames.map((name) => (
                <Chip key={name} label={name} size={"small"} />
              ))}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

const TemplateValidationDialog = ({
  open,
  onClose,
  reports = [],
  unvalidated = [],
  onProceed = null,
  proceedLabel = "Save anyway",
  requireReason = false,
  reason = "",
  onReasonChange = null,
  title = "Template validation",
  failureMessage = null,
}) => {
  const canProceed =
    !requireReason || (reason ?? "").trim().length >= MIN_REASON_LENGTH;
  const hasErrors = reports.some((report) => (report.errors ?? []).length > 0);
  const hasWarnings = reports.some(
    (report) => (report.warnings ?? []).length > 0
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={"md"}
      aria-labelledby={"template-validation-dialog-title"}
    >
      <DialogTitle id={"template-validation-dialog-title"}>{title}</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          {failureMessage && (
            <Alert severity={"warning"} variant={"outlined"}>
              {failureMessage}
            </Alert>
          )}
          {hasErrors && (
            <Alert severity={"error"} variant={"outlined"}>
              {
                "Errors will stop the affected template from generating documents. Correct the Word document before saving."
              }
            </Alert>
          )}
          {!hasErrors && hasWarnings && (
            <Alert severity={"warning"} variant={"outlined"}>
              {
                "Warnings will not stop documents from generating, but the values involved will print as blank."
              }
            </Alert>
          )}
          {reports.map((report, index) => (
            <ReportSection
              key={report.templateId ?? report.fileId ?? index}
              report={report}
            />
          ))}
          {unvalidated.length > 0 && (
            <Typography variant={"body2"} color={"text.secondary"}>
              {`${unvalidated.length} further template(s) were not validated because this save changed more than 10 at once.`}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack
          direction={"row"}
          gap={2}
          alignItems={"center"}
          justifyContent={"right"}
          sx={{ width: "100%" }}
        >
          {onProceed && requireReason && (
            <TextField
              type={"text"}
              InputLabelProps={{ shrink: true }}
              InputProps={{ spellCheck: "false" }}
              label={"Reason for override"}
              autoComplete={"off"}
              variant={"filled"}
              value={reason ?? ""}
              onChange={(e) => onReasonChange?.(e.target.value)}
              fullWidth
            />
          )}
          <Button onClick={onClose} color={"secondary"}>
            {onProceed ? "Cancel" : "Close"}
          </Button>
          {onProceed && (
            <Button
              onClick={onProceed}
              isDisabled={!canProceed}
              tooltip={"Enter a reason of at least four characters to proceed."}
              tooltipRequired={!canProceed}
            >
              {proceedLabel}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateValidationDialog;
